import { useState } from "react"
import { useIntl } from "react-intl"

import { Chip, StaffBodyTextS, StaffLabelTextM } from "@doktor-se/bones-ui"

import { getStaffLevels, StaffLevel } from "config"
import { useAppSelector, useClinicStaff } from "lib/hooks"

import styles from "./CloseMRCL.module.scss"

const capitalizeFirstLetter = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value

export interface CloseMRCLProps {
  selectedLevel?: StaffLevel
  onLevelChange?: (level: StaffLevel) => void
  showHeading?: boolean
}

const CloseMRCL = ({ selectedLevel, onLevelChange, showHeading = true }: CloseMRCLProps) => {
  const intl = useIntl()
  const defaultRole = useAppSelector(state => state.auth.defaultRole)
  const isClinicStaff = useClinicStaff()
  const roleLabelPrefix = isClinicStaff ? "label.role.clinic" : "label.role"
  // Map the user's default role to a staff level, fallback to "doctor" if no mapping
  const getDefaultStaffLevel = (): StaffLevel => {
    if (selectedLevel) return selectedLevel

    const role = defaultRole as StaffLevel
    const validStaffLevels = getStaffLevels(isClinicStaff)

    // If role is not available in the current staff type list, default to "doctor"
    if (role && !validStaffLevels.includes(role)) {
      return "doctor"
    }

    return role
  }

  const [level, setLevel] = useState<StaffLevel>(getDefaultStaffLevel())

  const handleLevelChange = (newLevel: StaffLevel) => {
    setLevel(newLevel)
    onLevelChange?.(newLevel)
  }

  const staffLevels = getStaffLevels(isClinicStaff)

  return (
    <div style={{ textAlign: "left", width: "100%" }}>
      {showHeading && isClinicStaff && (
        <StaffLabelTextM className={styles.heading}>
          {intl.formatMessage({
            id: "control.close.mrcl.title"
          })}
        </StaffLabelTextM>
      )}
      <div className={styles.questionContainer}>
        <StaffBodyTextS className={styles.question}>
          {intl.formatMessage({
            id: "control.close.mrcl.sub_title"
          })}
        </StaffBodyTextS>
      </div>
      <div className={styles.optionsContainer}>
        {staffLevels.map(staffLevel => (
          <div key={staffLevel}>
            <Chip isSelected={level === staffLevel} color="primary" onChange={() => handleLevelChange(staffLevel)}>
              {capitalizeFirstLetter(
                intl.formatMessage({
                  id: `${roleLabelPrefix}.${staffLevel}`
                })
              )}
            </Chip>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CloseMRCL
